import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/authStore';
import type {
  AuthResponse,
  VerificationRequest,
  VerificationResult,
  ConsentData,
  PersonalInfo,
  PaymentInitiation,
  PaymentConfig,
  AdminVerificationItem,
  AdminVerificationDetail,
  AdminDashboardStats,
  User,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = useAuthStore.getState().refreshToken;
        if (refreshToken) {
          const response = await axios.post<AuthResponse>(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });

          useAuthStore.getState().setAuth(response.data);

          originalRequest.headers.Authorization = `Bearer ${response.data.accessToken}`;
          return api(originalRequest);
        }
      } catch {
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  register: (data: {
    email: string;
    password: string;
    confirmPassword: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
  }) => api.post<{ message: string; user: User }>('/auth/register', data),

  login: (email: string, password: string) =>
    api.post<AuthResponse>('/auth/login', { email, password }),

  refresh: (refreshToken: string) =>
    api.post<AuthResponse>('/auth/refresh', { refreshToken }),

  logout: (refreshToken: string) =>
    api.post('/auth/logout', { refreshToken }),

  verifyEmail: (email: string, token: string) =>
    api.post('/auth/verify-email', { email, token }),

  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),

  resetPassword: (data: {
    email: string;
    token: string;
    newPassword: string;
    confirmPassword: string;
  }) => api.post('/auth/reset-password', data),

  changePassword: (data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => api.post('/auth/change-password', data),

  getMe: () => api.get<User>('/auth/me'),

  sendOtp: (phoneNumber: string) =>
    api.post('/auth/otp/send', { phoneNumber }),

  verifyOtp: (phoneNumber: string, code: string) =>
    api.post('/auth/otp/verify', { phoneNumber, code }),
};

// Verification API
export const verificationApi = {
  start: () =>
    api.post<{ requestId: string; status: string }>('/verification/start'),

  getRequests: () =>
    api.get<VerificationRequest[]>('/verification/requests'),

  getRequest: (id: string) =>
    api.get<VerificationRequest>(`/verification/requests/${id}`),

  recordConsent: (id: string, consent: ConsentData) =>
    api.post(`/verification/requests/${id}/consent`, consent),

  updatePersonalInfo: (id: string, info: PersonalInfo) =>
    api.post(`/verification/requests/${id}/personal-info`, info),

  updateGeolocation: (id: string, geolocation: {
    latitude?: number;
    longitude?: number;
    city?: string;
    region?: string;
    country?: string;
    ipAddress?: string;
  }) => api.post(`/verification/requests/${id}/geolocation`, geolocation),

  sendOtp: (id: string, phoneNumber: string) =>
    api.post(`/verification/requests/${id}/otp/send`, { phoneNumber }),

  verifyOtp: (id: string, phoneNumber: string, code: string) =>
    api.post(`/verification/requests/${id}/otp/verify`, { phoneNumber, code }),

  uploadDocument: (id: string, file: File, documentType: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    return api.post<{ documentId: string; message: string }>(
      `/verification/requests/${id}/document`,
      formData,
      { headers: { 'Content-Type': undefined } }
    );
  },

  getDocumentUrl: (requestId: string, documentId: string) =>
    api.get<{ url: string }>(`/verification/requests/${requestId}/document/${documentId}`),

  submit: (id: string) =>
    api.post(`/verification/requests/${id}/submit`),

  getStatus: (id: string) =>
    api.get(`/verification/requests/${id}/status`),

  getResult: (id: string) =>
    api.get<VerificationResult>(`/verification/requests/${id}/result`),

  validateId: (idNumber: string) =>
    api.post<{ isValid: boolean; dateOfBirth?: string; gender?: string }>(
      '/verification/validate-id',
      { idNumber }
    ),
};

// Payment API
export const paymentApi = {
  initiate: (verificationRequestId: string, paymentMethod: string) =>
    api.post<PaymentInitiation>('/payment/initiate', {
      verificationRequestId,
      paymentMethod,
    }),

  getStatus: (paymentId: string) =>
    api.get<{
      paymentId: string;
      status: string;
      amount: number;
      paymentMethod: string;
      completedAt?: string;
    }>(`/payment/status/${paymentId}`),

  getConfig: () =>
    api.get<PaymentConfig>('/payment/config'),
};

// Admin API
export const adminApi = {
  getRequests: (page = 1, pageSize = 20) =>
    api.get<AdminVerificationItem[]>(`/admin/requests?page=${page}&pageSize=${pageSize}`),

  getRequestDetail: (id: string) =>
    api.get<AdminVerificationDetail>(`/admin/requests/${id}`),

  getDocumentUrl: (requestId: string, documentId: string) =>
    api.get<{ url: string }>(`/admin/requests/${requestId}/document/${documentId}`),

  approve: (id: string, notes?: string) =>
    api.post(`/admin/requests/${id}/approve`, { adminNotes: notes }),

  reject: (id: string, reason: string, notes?: string) =>
    api.post(`/admin/requests/${id}/reject`, {
      rejectionReason: reason,
      adminNotes: notes,
    }),

  confirmPayment: (id: string, notes?: string) =>
    api.post(`/admin/requests/${id}/confirm-payment`, { adminNotes: notes }),

  getStats: () =>
    api.get<AdminDashboardStats>('/admin/stats'),
};

// Support API
export const supportApi = {
  submitContact: (data: {
    name: string;
    email: string;
    subject: string;
    message: string;
  }) => api.post<{ message: string }>('/support/contact', data),
};

export default api;
